import React, { useState } from 'react';
import axios from 'axios';
import StatusChangeModal from './StatusChangeModal';

const actionColorMap = [
  'Accepted',
  'Completed',
  'Declined',
  'Deferred',
  'Go/No-Go',
  'Pending',
  'Scheduled',
  'Working',
];

const projectColorMap = [
  'Awarded',
  'Completed',
  'Cultivating',
  'Declined',
  'Deferred',
  'Go/No-Go',
  'Hot Mess',
  'LOI Pending',
  'Never Seek',
  'Pending',
  'Planned',
  'Preliminary',
  'Withdrawn',
  'Working',
];

interface CellProp {
  cell: { value: any };
  row: { original: any };
}

// Tables re-use cell instances across data refetches (rows can shift under a
// mounted cell), so the row value must stay the source of truth. Local state
// only holds the user's own optimistic change, keyed to the entity it was made
// for — it is ignored as soon as a different entity renders through this cell.
interface StatusOverride {
  entityKey: string;
  status: string;
}

const selectedStatus = (
  override: StatusOverride | null,
  entityKey: string,
  value: any,
) =>
  override && override.entityKey === entityKey ? override.status : value ?? '';

const StatusSelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (status: string) => void;
}) => (
  <div className="deadline-field--container">
    <select
      className="status-inline-field"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {!options.includes(value) && (
        <option value={value} disabled>
          {value || '—'}
        </option>
      )}
      {options.map((status) => (
        <option key={status} value={status}>
          {status}
        </option>
      ))}
    </select>
  </div>
);

export const StatusCellDeadline = ({
  cell: { value },
  row: {
    original: { entity_id, deadline_type },
  },
}: CellProp) => {
  const [override, setOverride] = useState<StatusOverride | null>(null);

  const colorMap = deadline_type === 'action' ? actionColorMap : projectColorMap;
  const entityKey = `${deadline_type}_${entity_id}`;
  const selected = selectedStatus(override, entityKey, value);

  const changeStatus = (status: string) => {
    setOverride({ entityKey, status });
    if (deadline_type === 'project') {
      axios.put(`/project/update`, { id: entity_id, status });
    } else {
      axios.put(`/action/${entity_id}`, { status });
    }
  };

  return (
    <StatusSelect value={selected} options={colorMap} onChange={changeStatus} />
  );
};

export const StatusCellProjectDeadline = ({
  cell: { value },
  row: {
    original: { id },
  },
}: CellProp) => {
  const [override, setOverride] = useState<StatusOverride | null>(null);

  const entityKey = String(id);
  const selected = selectedStatus(override, entityKey, value);

  const changeStatus = (status: string) => {
    setOverride({ entityKey, status });
    axios.put(`/action/${id}`, { status });
  };

  return (
    <StatusSelect
      value={selected}
      options={actionColorMap}
      onChange={changeStatus}
    />
  );
};

export const StatusCellProject = ({
  cell: { value },
  row: {
    original: { id },
  },
}: CellProp) => {
  const [override, setOverride] = useState<StatusOverride | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const entityKey = String(id);
  const selected = selectedStatus(override, entityKey, value);

  const updateStatus = (status: string) => {
    setOverride({ entityKey, status });
    axios.put(`/project/update`, { id, status });
  };

  const handleStatusChange = (status: string) => {
    if (['Declined', 'Deferred', 'Never Seek'].includes(status)) {
      setPendingStatus(status);
      setShowConfirmModal(true);
    } else {
      updateStatus(status);
    }
  };

  const handleConfirm = () => {
    updateStatus(pendingStatus);
    setShowConfirmModal(false);
  };

  return (
    <>
      <StatusSelect
        value={selected}
        options={projectColorMap}
        onChange={handleStatusChange}
      />
      <StatusChangeModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        status={pendingStatus}
      />
    </>
  );
};
